import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import DownloadIcon from '@mui/icons-material/Download'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import StopCircleIcon from '@mui/icons-material/StopCircle'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  DialogContentText,
  CircularProgress,
  Divider,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { useState } from 'react'

import { api, ApiError, type Job, type JobFile, type JobState } from '../api/client'
import { relativeTime } from '../lib/time'
import ConfirmDialog from './ConfirmDialog'
import FilePlayer from './FilePlayer'

const LOOK: Record<JobState, { label: string; color: 'success' | 'error' | 'info' | 'default' }> = {
  submitting: { label: 'Submitting', color: 'info' },
  submit_failed: { label: 'Not submitted', color: 'error' },
  queued: { label: 'Queued', color: 'info' },
  running: { label: 'Running', color: 'info' },
  completed: { label: 'Done', color: 'success' },
  failed: { label: 'Failed', color: 'error' },
  cancelled: { label: 'Cancelled', color: 'default' },
  unknown: { label: 'Unknown', color: 'default' },
}

function megabytes(bytes: number): string {
  return `${(bytes / 1_048_576).toFixed(1)} MB`
}

function fileLabel(file: JobFile): string {
  if (file.kind === 'original') return 'Source'
  // What the covers were actually made from, once variance altered it. Without
  // its own case this fell through to the cover branch and rendered as
  // "Cover · " with an empty model.
  if (file.kind === 'varied_source') return 'Varied source'
  if (file.kind === 'stem') return `Stem · ${file.stem ?? ''}`
  return `Cover · ${file.model ?? ''}`
}

interface Props {
  job: Job
  canModify: boolean
  onChanged: () => void
}

export default function JobRow({ job, canModify, onChanged }: Props) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  /** Which confirmation is open, if any. Both actions here are one-way. */
  const [asking, setAsking] = useState<'cancel' | 'delete' | null>(null)
  const look = LOOK[job.state]

  const storedBytes = job.files.reduce((total, file) => total + file.size, 0)
  /**
   * A terminal job does not necessarily have results. Cancelled, failed and
   * submit-failed jobs are all terminal, and only a completed one ever mirrors
   * files -- so for those, and for anything whose results have expired, there is
   * nothing to delete except the order itself.
   */
  const hasResults = job.files.length > 0

  async function run(action: () => Promise<unknown>) {
    setBusy(true)
    setError(null)
    try {
      await action()
      onChanged()
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'That did not work.')
    } finally {
      setBusy(false)
    }
  }

  const progressNote = job.is_terminal
    ? relativeTime(job.finished_at ?? job.created_at)
    : (job.stage ??
      (job.queue_position && job.queue_position > 0 ? `#${job.queue_position} in line` : 'Waiting'))

  return (
    <Accordion disableGutters elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: '100%', pr: 1 }}>
          {!job.is_terminal && <CircularProgress size={16} />}
          <Typography sx={{ flexGrow: 1, minWidth: 0 }} noWrap title={job.source_filename}>
            {job.source_filename}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
            {job.username}
          </Typography>
          <Chip size="small" color={look.color} variant="outlined" label={look.label} />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ minWidth: 120, textAlign: 'right' }}
          >
            {progressNote}
          </Typography>
        </Stack>
      </AccordionSummary>

      <AccordionDetails>
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}

          {job.error && (
            <Alert severity={job.state === 'cancelled' ? 'info' : 'error'}>{job.error}</Alert>
          )}

          {job.state === 'completed' && !job.results_mirrored && !job.results_expired && (
            <Stack direction="row" spacing={1.5} alignItems="center">
              <CircularProgress size={16} />
              <Typography variant="body2">
                Copying the results off the GPU. They will appear here shortly.
              </Typography>
            </Stack>
          )}

          {job.results_expired && (
            <Alert severity="info">
              These results passed the retention window and were deleted. The record of the order is
              kept.
            </Alert>
          )}

          {job.files.length > 0 && (
            <Stack divider={<Divider flexItem />}>
              {job.files.map((file) => (
                <Stack
                  key={file.id}
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  sx={{ py: 1 }}
                >
                  <FilePlayer jobId={job.id} fileId={file.id} ready={file.has_preview} />
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography variant="body2" noWrap title={file.name}>
                      {file.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {fileLabel(file)} · {megabytes(file.size)}
                    </Typography>
                  </Box>
                  <Tooltip title="Download the full-quality WAV">
                    <IconButton
                      component={Link}
                      href={api.fileUrl(job.id, file.id)}
                      download={file.name}
                      size="small"
                    >
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              ))}
            </Stack>
          )}

          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1 }}>
              {describeParams(job)}
            </Typography>

            {!job.is_terminal && job.gpu_job_id && (
              <Button
                size="small"
                color="warning"
                startIcon={<StopCircleIcon />}
                disabled={busy || !canModify}
                onClick={() => setAsking('cancel')}
              >
                Cancel
              </Button>
            )}
            {job.is_terminal && (
              <Button
                size="small"
                color="error"
                startIcon={<DeleteOutlineIcon />}
                disabled={busy || !canModify}
                onClick={() => setAsking('delete')}
              >
                Delete
              </Button>
            )}
            {!canModify && (
              <Typography variant="caption" color="text.secondary">
                Only {job.username} or a sysadmin can change this.
              </Typography>
            )}
          </Stack>

          <ConfirmDialog
            open={asking === 'cancel'}
            title="Stop this job?"
            destructive
            busy={busy}
            confirmLabel="Stop Job"
            body={
              <Stack spacing={1}>
                <DialogContentText>{job.source_filename}</DialogContentText>
                <DialogContentText variant="body2">
                  The GPU time already spent on it is not recoverable, and it will produce no
                  results. Ordering it again starts from the beginning.
                </DialogContentText>
              </Stack>
            }
            onCancel={() => setAsking(null)}
            onConfirm={() => {
              setAsking(null)
              void run(() => api.cancelJob(job.id))
            }}
          />

          <ConfirmDialog
            open={asking === 'delete'}
            title={hasResults ? 'Delete these results?' : 'Delete this order?'}
            destructive
            busy={busy}
            confirmLabel="Delete"
            body={
              <Stack spacing={1}>
                {/* Named and measured: the queue is shared, so the row you
                    clicked may not be the row you meant. */}
                <DialogContentText>{job.source_filename}</DialogContentText>
                {hasResults ? (
                  <>
                    <DialogContentText variant="body2">
                      {job.files.length} file{job.files.length === 1 ? '' : 's'} ·{' '}
                      {megabytes(storedBytes)} · ordered by {job.username}
                    </DialogContentText>
                    <DialogContentText variant="body2">
                      The GPU's copy was deleted once these were mirrored here, so this cannot be
                      undone.
                    </DialogContentText>
                  </>
                ) : (
                  // Saying "0 files, 0.0 MB" here read as "nothing is at stake",
                  // when what goes is the order's own record in the queue.
                  <DialogContentText variant="body2">
                    Ordered by {job.username}.{' '}
                    {job.results_expired
                      ? 'Its results have already expired, so'
                      : 'It produced no results, so'}{' '}
                    this removes the order from the queue and nothing else. It cannot be undone.
                  </DialogContentText>
                )}
              </Stack>
            }
            onCancel={() => setAsking(null)}
            onConfirm={() => {
              setAsking(null)
              void run(() => api.deleteJob(job.id))
            }}
          />
        </Stack>
      </AccordionDetails>
    </Accordion>
  )
}

function describeParams(job: Job): string {
  const params = job.params as {
    separation_engine?: string
    targets?: string[]
    dit_models?: string[]
  }
  const parts: string[] = []
  if (params.separation_engine) {
    parts.push(
      params.separation_engine === 'none'
        ? 'no separation'
        : `${params.separation_engine} separation`,
    )
  }
  if (params.targets?.length) parts.push(params.targets.join(', '))
  if (params.dit_models?.length) {
    parts.push(`${params.dit_models.length} model${params.dit_models.length === 1 ? '' : 's'}`)
  }
  if (job.started_at && job.finished_at) {
    const seconds = Math.round(
      (new Date(job.finished_at).getTime() - new Date(job.started_at).getTime()) / 1000,
    )
    if (seconds > 0) parts.push(`${seconds}s on the GPU`)
  }
  return parts.join(' · ')
}
